import { KitRequestSchema } from 'types/kitting';
import {
  warehouseStorageName,
  warehouseDefaults,
} from 'types/warehouseSettings';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { usePickedHandlingRequestsNoInvalidateMutation } from 'store/slices/clientV2/handlingRequests';
import useLocalStorage from 'hooks/useLocalStorage';
import useSnackbar from 'hooks/useSnackbar';
import {
  HandlingRequestType,
  StockLocation,
  StockLocationType,
  StockLotCRUDSchema,
  StockLotQuerySchema,
} from 'types/inventory';
import { WarehouseKitRequest } from 'views/warehouse/types';
import { Org } from 'types/org';
import {
  StockLocationPurpose,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';
import { useWarehouseKitRequest } from 'contexts/WarehouseKitRequestContext';
import { useWarehouseWorkstationContext } from 'contexts/WarehouseWorkstationContext';
import { useScannerContext } from 'contexts/ScannerContext';
import { useGetPickingLocations } from 'views/warehouse/MobilePicking/hooks/useGetPickingLocations';
import { useGetStockLotsToPick } from 'views/warehouse/MobilePicking/hooks/useGetStockLotsToPick';
import {
  LocationWithStockLots,
  PickListStockLot,
} from 'views/warehouse/MobilePicking/types';
import { buildFacilityPickingLocations } from 'views/warehouse/MobilePicking/utils';
import { getNextHandlingRequest } from 'views/warehouse/utils';
import { handleErr } from 'utils/functions';
import { Team } from 'types/properties';
import { useMoveWarningContext } from 'views/warehouse/MoveWarningContext';

type HandleSetFilters = ({
  handlingRequestTypes,
  orgIds,
  teamIds,
}: {
  handlingRequestTypes: HandlingRequestType[];
  orgIds: Org['id'][];
  teamIds: Team['id'][];
}) => void;
type ResetFilters = () => void;

type MobilePickingContextType = {
  pickListLocations: LocationWithStockLots[] | null;
  isFetchingPickListLocations: boolean;
  isLoadingPickListLocations: boolean;
  kitRequests: WarehouseKitRequest[] | null;
  isLoadingKitRequests: boolean;
  selectedKitRequest: WarehouseKitRequest | null;
  handleSelectedKitRequestChange: (
    kitRequestId: KitRequestSchema['id'] | null
  ) => void;
  handleSetFilters: HandleSetFilters;
  resetFilters: ResetFilters;
  orgIdsFilter: Org['id'][];
  handlingRequestTypesFilter: HandlingRequestType[];
  teamIdsFilter: Team['id'][];
  parentLocation: StockLocation['id'];
  clearSelectedKitRequest: () => void;
  refetchKitRequests: () => void;
  pickedStockLots: StockLotQuerySchema['id'][];
  handleRefresh: () => void;
  isPickingHandlingRequestsLoading: boolean;
  pickToBins: StockLocationSchemaLite[];
  targetLocation: StockLocationSchemaLite | null;
  pickingMode: PickingMode;
  togglePickingMode: () => void;
  stockLotsToPickMap: Map<string, PickListStockLot>;
  selectedStockLot: PickListStockLot | null;
  setSelectedStockLot: (stockLot: PickListStockLot) => void;
  stockLotPickingLocations: LocationWithStockLots[];
  binPickingLocations: LocationWithStockLots[];
};

export enum PickingMode {
  BIN_PICKING = 'bin-picking',
  STOCK_LOT_PICKING = 'stock-lot-picking',
}

const MobilePickingContext = createContext<
  MobilePickingContextType | undefined
>(undefined);

export const MobilePickingProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const { validateMoveAndShowWarning } = useMoveWarningContext();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const [pickingMode, setPickingMode] = useState<PickingMode>(
    PickingMode.BIN_PICKING
  );

  const [selectedStockLot, setSelectedStockLot] =
    useState<PickListStockLot | null>(null);

  const [selectedKitRequestId, setSelectedKitRequestId] = useState<
    KitRequestSchema['id'] | null
  >(null);
  const [handlingRequestTypesFilter, setHandlingRequestTypesFilter] = useState<
    HandlingRequestType[]
  >([]);
  const [orgIdsFilter, setOrgIdsFilter] = useState<Org['id'][]>([]);
  const [teamIdsFilter, setTeamIdsFilter] = useState<Team['id'][]>([]);
  const [pickedStockLots, setPickedStockLots] = useState<
    StockLotQuerySchema['id'][]
  >([]);
  const [
    pickedHandlingRequests,
    { isLoading: isPickingHandlingRequestsLoading },
  ] = usePickedHandlingRequestsNoInvalidateMutation();

  const {
    kitRequests: warehouseKitRequests,
    isLoadingKitRequests,
    refetchKitRequests,
  } = useWarehouseKitRequest();

  const { filteredWorkstationLocations: workstationLocations, targetLocation } =
    useWarehouseWorkstationContext();

  const {
    stockLotsToPick,
    isFetchingHandlingRequests,
    isLoadingHandlingRequests,
    stockLotLocationsIds,
    stockLotsToPickMap,
    refetchHandlingRequests,
  } = useGetStockLotsToPick();

  const kitRequests =
    warehouseKitRequests.length > 0 ? warehouseKitRequests : null;

  const {
    stockLocations,
    isFetching: isFetchingStockLocations,
    isLoading: isLoadingStockLocations,
  } = useGetPickingLocations(stockLotLocationsIds);

  const isFetchingPickListLocations =
    isFetchingHandlingRequests || isFetchingStockLocations;

  const isLoadingPickListLocations =
    isLoadingHandlingRequests || isLoadingStockLocations;

  const { stockLotPickingLocations, binPickingLocations } = useMemo(
    () =>
      buildFacilityPickingLocations(
        stockLocations,
        stockLotsToPick,
        settings.facility
      ),
    [stockLocations, stockLotsToPick, settings.facility]
  );

  const pickListLocations = useMemo(
    () =>
      pickingMode === PickingMode.BIN_PICKING
        ? binPickingLocations
        : stockLotPickingLocations,
    [pickingMode, binPickingLocations, stockLotPickingLocations]
  );

  const pickStockLot = async (
    stockLot: PickListStockLot,
    pickToLocation: StockLocationSchemaLite
  ) => {
    if (!stockLot.handlingRequests?.length) {
      dispatchErrorSnackbar('No handling request found for stock lot');
      return;
    }
    try {
      await pickedHandlingRequests({
        ids: stockLot.handlingRequests.map(
          (handlingRequest) => handlingRequest.id
        ),
        stockLocation: pickToLocation.id,
      }).unwrap();
      const nextRequest = getNextHandlingRequest(stockLot.handlingRequests);
      if (nextRequest) {
        validateMoveAndShowWarning(stockLot, pickToLocation, {
          ...nextRequest,
          stockLot: nextRequest?.stockLot.id,
        });
      }
      setSelectedStockLot(null);
      setPickedStockLots((prev) => [...prev, stockLot.id]);
      dispatchSuccessSnackbar('Stock lot picked for handling request');
    } catch (error) {
      handleErr(error, (errMessage) => dispatchErrorSnackbar(errMessage));
    }
  };

  const handleSelectedKitRequestChange = (
    kitRequestId: KitRequestSchema['id'] | null
  ) => {
    setSelectedKitRequestId(kitRequestId);
  };

  const clearSelectedKitRequest = () => {
    setSelectedKitRequestId(null);
  };

  const handleSetFilters: HandleSetFilters = ({
    handlingRequestTypes,
    orgIds,
    teamIds,
  }) => {
    setHandlingRequestTypesFilter(handlingRequestTypes);
    setOrgIdsFilter(orgIds);
    setTeamIdsFilter(teamIds);
  };

  const resetFilters = () => {
    setHandlingRequestTypesFilter([]);
    setOrgIdsFilter([]);
  };

  const handleRefresh = () => {
    refetchKitRequests();
    refetchHandlingRequests();
  };

  const selectedKitRequest = useMemo(() => {
    if (!selectedKitRequestId || !kitRequests) return null;
    return (
      kitRequests.find(
        (kitRequest) => kitRequest.id === selectedKitRequestId
      ) || null
    );
  }, [selectedKitRequestId, kitRequests]);

  const pickToBins = useMemo(
    () =>
      workstationLocations.filter(
        (location) =>
          location.locationType === StockLocationType.ESD_BIN &&
          location.purpose !== StockLocationPurpose.STORAGE
      ),
    [workstationLocations]
  );

  const togglePickingMode = () => {
    setPickingMode((prev) =>
      prev === PickingMode.BIN_PICKING
        ? PickingMode.STOCK_LOT_PICKING
        : PickingMode.BIN_PICKING
    );
  };

  const handleScannedStockLocation = (
    scannedStockLocation: StockLocationSchemaLite
  ) => {
    if (
      scannedStockLocation.locationType === StockLocationType.BIN_SECTION &&
      selectedStockLot
    ) {
      pickStockLot(selectedStockLot, scannedStockLocation);
    }
  };

  const handleScannedStockLot = (scannedStockLot: StockLotCRUDSchema) => {
    const matchedStockLot =
      stockLotsToPick.find((stockLot) => stockLot.id === scannedStockLot.id) ||
      null;
    if (!targetLocation) {
      setSelectedStockLot(matchedStockLot);
    }
    if (targetLocation && matchedStockLot)
      pickStockLot(matchedStockLot, targetLocation);
  };

  useScannerContext({
    handleScannedStockLot,
    handleScannedStockLocation,
  });

  return (
    <MobilePickingContext.Provider
      value={{
        pickListLocations,
        isFetchingPickListLocations,
        isLoadingPickListLocations,
        kitRequests,
        isLoadingKitRequests,
        selectedKitRequest,
        handleSelectedKitRequestChange,
        handleSetFilters,
        resetFilters,
        orgIdsFilter,
        teamIdsFilter,
        handlingRequestTypesFilter,
        parentLocation: settings.facility,
        clearSelectedKitRequest,
        refetchKitRequests,
        pickedStockLots,
        handleRefresh,
        isPickingHandlingRequestsLoading,
        pickToBins,
        targetLocation,
        pickingMode,
        togglePickingMode,
        stockLotsToPickMap,
        selectedStockLot,
        setSelectedStockLot,
        stockLotPickingLocations,
        binPickingLocations,
      }}
    >
      {children}
    </MobilePickingContext.Provider>
  );
};

export const useMobilePickingContext = () => {
  const context = useContext(MobilePickingContext);
  if (context === undefined) {
    throw new Error(
      'useMobilePickingContext must be used within a MobilePickingProvider'
    );
  }
  return context;
};
